/**
 * Copyright © 2025 Delicious AI, LLC
 *
 */

import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import useAuth from '@/hooks/useAuth'
import { usePermissions } from '@/hooks/usePermissions'

function PermissionGuard({
  children,
  requiredPermissions,
  navigateTo = '/not-authorized',
}: {
  children?: React.ReactNode
  requiredPermissions: string[]
  navigateTo?: string
}) {
  const auth = useAuth()
  const location = useLocation()
  const { hasPermission, loading } = usePermissions()

  if (!auth.isInitialized || loading) {
    return null
  }

  if (requiredPermissions.length > 0) {
    const hasRequiredPermissions = requiredPermissions.every(permission =>
      hasPermission(permission),
    )
    if (!hasRequiredPermissions) {
      return (
        <Navigate to={navigateTo} state={{ from: location.pathname }} replace />
      )
    }
  }

  return <>{children}</>
}

export default PermissionGuard
